/**
 * transactions.js
 * Historical Analytics Layer — Transaction-based provider.
 *
 * Owns: purchase_orders, cf_payment_entries, cash_in_planning, and the
 *   five Actual Cost sources (ipc_line_items, payroll_entries,
 *   store_issues, petty_cash_vouchers, accrual_entries).
 *
 * Responsibility: given a project and a date boundary, reconstruct
 * "what the cumulative total was as of that date" by filtering each
 * source's own transaction date field. This is possible without any
 * new snapshot table because every source already carries a real
 * transaction date — reconstruction is pure filtering + summation of
 * data that already exists.
 *
 * This file is STATELESS with respect to "what is the current date" —
 * it never computes "today" or "this month". It only filters relative
 * to whatever date boundaries the caller supplies. Callers (Dashboard,
 * Executive Report, future Analytics) are responsible for building the
 * Period object; this file's responsibility begins only after that
 * period has been defined.
 *
 * No new tables. No duplicated financial logic — the Actual Cost
 * aggregation here mirrors the exact same five-source pattern
 * MonthlyActualCost.jsx already uses (same fields, same period
 * derivation), so this can never disagree with it about what "Actual
 * Cost as of a given month" means.
 */
import { supabase } from './supabase'

// ─── Actual Cost ────────────────────────────────────────────────────────

/**
 * Sums Actual Cost across all five sources, filtered to entries whose
 * own transaction date falls on or before asOfDate. Mirrors
 * MonthlyActualCost.jsx's period derivation exactly (dateStr.slice(0,7)
 * for IPC/Store/Petty Cash/Accrual; year+month fields for Payroll) so
 * the two can never drift apart on what "as of a given month" means.
 *
 * @param {string} projectId
 * @param {string} asOfDate - 'YYYY-MM-DD'
 * @returns {Promise<{ value: number, quality: string }>}
 */
async function resolveActualAsOf(projectId, asOfDate) {
  if (!projectId || !asOfDate) return { value: 0, quality: 'unavailable' }

  const [ipcRes, payrollRes, storeRes, pettyRes, accrualRes] = await Promise.all([
    supabase.from('ipc_line_items')
      .select('this_period_amount, actual_cost_date, ipc_certificates!inner(project_id, status)')
      .eq('ipc_certificates.project_id', projectId)
      .eq('ipc_certificates.status', 'approved')
      .lte('actual_cost_date', asOfDate),
    supabase.from('payroll_entries')
      .select('total_salary, year, month, period_type')
      .eq('project_id', projectId).eq('status', 'approved'),
    supabase.from('store_issues')
      .select('amount, issued_date')
      .eq('project_id', projectId)
      .lte('issued_date', asOfDate),
    supabase.from('petty_cash_vouchers')
      .select('amount_exc_vat, actual_cost_date')
      .eq('project_id', projectId)
      .lte('actual_cost_date', asOfDate),
    supabase.from('accrual_entries')
      .select('amount, actual_cost_date')
      .eq('project_id', projectId)
      .lte('actual_cost_date', asOfDate),
  ])

  const failures = [ipcRes, payrollRes, storeRes, pettyRes, accrualRes].filter(r => r.error)
  if (failures.length > 0) {
    failures.forEach(f => console.error('transactions.js: resolveActualAsOf source failed', f.error))
  }

  const ipcTotal = (ipcRes.data || []).reduce((s, r) => s + (parseFloat(r.this_period_amount) || 0), 0)
  const storeTotal = (storeRes.data || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
  const pettyTotal = (pettyRes.data || []).reduce((s, r) => s + (parseFloat(r.amount_exc_vat) || 0), 0)
  const accrualTotal = (accrualRes.data || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)

  // Payroll has no single date column — it derives a period from
  // year+month (or just year, for period_type 'yearly'), exactly as
  // MonthlyActualCost.jsx does. Filtered here in JS against the same
  // asOfDate boundary since the source table itself isn't a simple
  // date column we can push into the query.
  const asOfPeriod = asOfDate.slice(0, 7) // 'YYYY-MM'
  const payrollTotal = (payrollRes.data || []).reduce((s, r) => {
    const mo = parseInt(r.month) || 1
    const yr = parseInt(r.year) || 0
    const period = r.period_type === 'yearly' ? `${yr}-01` : `${yr}-${String(mo).padStart(2, '0')}`
    if (period > asOfPeriod) return s
    return s + (parseFloat(r.total_salary) || 0)
  }, 0)

  const value = ipcTotal + payrollTotal + storeTotal + pettyTotal + accrualTotal
  const quality = failures.length === 0 ? 'complete' : (failures.length < 5 ? 'partial' : 'unavailable')

  return { value, quality }
}

// ─── Commitment ─────────────────────────────────────────────────────────

/**
 * Sums Commitment (approved/paid/received/closed Purchase Orders) whose
 * issue_date falls on or before asOfDate. Same status filter Dashboard.jsx
 * already uses for live Committed — only the date boundary is new.
 *
 * Note (documented limitation, consistent with the existing Committed
 * gap already recorded in the architecture contract): this does not yet
 * include Subcontracts. When Subcontracts are added to Committed
 * elsewhere, the same issue_date-style filter should be added here too —
 * this function should not silently diverge from that future fix.
 *
 * @param {string} projectId
 * @param {string} asOfDate - 'YYYY-MM-DD'
 * @returns {Promise<{ value: number, quality: string }>}
 */
async function resolveCommitmentAsOf(projectId, asOfDate) {
  if (!projectId || !asOfDate) return { value: 0, quality: 'unavailable' }

  const { data, error } = await supabase
    .from('purchase_orders')
    .select('total_exc_vat')
    .eq('project_id', projectId)
    .in('status', ['approved', 'paid', 'received', 'closed'])
    .lte('issue_date', asOfDate)

  if (error) {
    console.error('transactions.js: resolveCommitmentAsOf failed', error)
    return { value: 0, quality: 'unavailable' }
  }

  const value = (data || []).reduce((s, r) => s + (parseFloat(r.total_exc_vat) || 0), 0)
  return { value, quality: 'complete' }
}

// ─── Cash In ────────────────────────────────────────────────────────────

/**
 * Sums Client Invoice (cash_in_planning.client_invoice — the same
 * source Dashboard.jsx now uses for live "Invoiced to Client", per the
 * Scheduling-owned fix) for months on or before asOfDate's month.
 * period_month is a monthly field, so filtering is by month, not by
 * exact day.
 *
 * @param {string} projectId
 * @param {string} asOfDate - 'YYYY-MM-DD'
 * @returns {Promise<{ value: number, quality: string }>}
 */
async function resolveCashInAsOf(projectId, asOfDate) {
  if (!projectId || !asOfDate) return { value: 0, quality: 'unavailable' }

  const { data, error } = await supabase
    .from('cash_in_planning')
    .select('client_invoice, period_month')
    .eq('project_id', projectId)
    .lte('period_month', asOfDate)

  if (error) {
    console.error('transactions.js: resolveCashInAsOf failed', error)
    return { value: 0, quality: 'unavailable' }
  }

  const value = (data || []).reduce((s, r) => s + (parseFloat(r.client_invoice) || 0), 0)
  return { value, quality: 'complete' }
}

// ─── Cash Out ───────────────────────────────────────────────────────────

/**
 * Sums Cash Out (cf_payment_entries.amount_inc_vat) whose own date
 * falls on or before asOfDate.
 *
 * @param {string} projectId
 * @param {string} asOfDate - 'YYYY-MM-DD'
 * @returns {Promise<{ value: number, quality: string }>}
 */
async function resolveCashOutAsOf(projectId, asOfDate) {
  if (!projectId || !asOfDate) return { value: 0, quality: 'unavailable' }

  const { data, error } = await supabase
    .from('cf_payment_entries')
    .select('amount_inc_vat, date')
    .eq('project_id', projectId)
    .lte('date', asOfDate)

  if (error) {
    console.error('transactions.js: resolveCashOutAsOf failed', error)
    return { value: 0, quality: 'unavailable' }
  }

  const value = (data || []).reduce((s, r) => s + (parseFloat(r.amount_inc_vat) || 0), 0)
  return { value, quality: 'complete' }
}

// ─── Public entry point ──────────────────────────────────────────────────

/**
 * Public entry point for this provider. Given a project and a
 * HistoricalContext (currentPeriodEnd / previousPeriodEnd — both
 * 'YYYY-MM-DD', supplied entirely by the caller), reconstructs Actual,
 * Commitment, Cash In, and Cash Out at both boundaries.
 *
 * Does NOT compute delta/deltaPct here — that belongs to the
 * Aggregator, which owns the unified output contract shared across
 * providers (this file and releases.js).
 *
 * @param {string} projectId
 * @param {import('./historyAggregator').HistoricalContext} context - only
 *   context.calendar.currentPeriodEnd / previousPeriodEnd are read here.
 * @returns {Promise<{
 *   actual:     { current: number|null, previous: number|null, asOfCurrent: string|null, asOfPrevious: string|null, quality: string },
 *   commitment: { current: number|null, previous: number|null, asOfCurrent: string|null, asOfPrevious: string|null, quality: string },
 *   cashIn:     { current: number|null, previous: number|null, asOfCurrent: string|null, asOfPrevious: string|null, quality: string },
 *   cashOut:    { current: number|null, previous: number|null, asOfCurrent: string|null, asOfPrevious: string|null, quality: string },
 * }>}
 */
export async function getTransactionHistory(projectId, context) {
  const currentPeriodEnd = context?.calendar?.currentPeriodEnd
  const previousPeriodEnd = context?.calendar?.previousPeriodEnd
  if (!projectId || !currentPeriodEnd || !previousPeriodEnd) {
    const empty = { current: null, previous: null, asOfCurrent: null, asOfPrevious: null, quality: 'unavailable' }
    return { actual: { ...empty }, commitment: { ...empty }, cashIn: { ...empty }, cashOut: { ...empty } }
  }

  const [
    actualCurrent, actualPrevious,
    commitmentCurrent, commitmentPrevious,
    cashInCurrent, cashInPrevious,
    cashOutCurrent, cashOutPrevious,
  ] = await Promise.all([
    resolveActualAsOf(projectId, currentPeriodEnd),
    resolveActualAsOf(projectId, previousPeriodEnd),
    resolveCommitmentAsOf(projectId, currentPeriodEnd),
    resolveCommitmentAsOf(projectId, previousPeriodEnd),
    resolveCashInAsOf(projectId, currentPeriodEnd),
    resolveCashInAsOf(projectId, previousPeriodEnd),
    resolveCashOutAsOf(projectId, currentPeriodEnd),
    resolveCashOutAsOf(projectId, previousPeriodEnd),
  ])

  const combineQuality = (a, b) => {
    if (a === 'complete' && b === 'complete') return 'complete'
    if (a === 'unavailable' && b === 'unavailable') return 'unavailable'
    return 'partial'
  }

  // asOf for Transaction-based metrics equals the calendar boundary
  // itself — unlike releases.js, there is no "search and land on
  // whatever was found" here; the filter cutoff IS the effective date.
  // Included for shape consistency across all metrics in the unified
  // contract, per the decision that asOf is a business result field,
  // not something a consumer should have to derive per provider type.
  return {
    actual: {
      current: actualCurrent.value, previous: actualPrevious.value,
      asOfCurrent: currentPeriodEnd, asOfPrevious: previousPeriodEnd,
      quality: combineQuality(actualCurrent.quality, actualPrevious.quality),
    },
    commitment: {
      current: commitmentCurrent.value, previous: commitmentPrevious.value,
      asOfCurrent: currentPeriodEnd, asOfPrevious: previousPeriodEnd,
      quality: combineQuality(commitmentCurrent.quality, commitmentPrevious.quality),
    },
    cashIn: {
      current: cashInCurrent.value, previous: cashInPrevious.value,
      asOfCurrent: currentPeriodEnd, asOfPrevious: previousPeriodEnd,
      quality: combineQuality(cashInCurrent.quality, cashInPrevious.quality),
    },
    cashOut: {
      current: cashOutCurrent.value, previous: cashOutPrevious.value,
      asOfCurrent: currentPeriodEnd, asOfPrevious: previousPeriodEnd,
      quality: combineQuality(cashOutCurrent.quality, cashOutPrevious.quality),
    },
  }
}
